'use client';

import parse, {
  Element as ParserElement,
  domToReact,
  type HTMLReactParserOptions,
} from 'html-react-parser';
import DOMPurify from 'isomorphic-dompurify';
import type { ReactNode } from 'react';
import { Anchor } from '../../Anchor';
import type { CustomComponentTypes } from '../../Marketing/ContentRenderer';
import { trimTitleToSlug } from './trimTitleToSlug';

export type TableOfContents = {
  tag: string;
  text: string;
}[];

/**
 * Components that are used to silence the missing component warnings
 * but are not actually rendered in the page
 *
 */
export const tableOfContentsPlaceholderComponents: {
  types: Record<CustomComponentTypes, () => ReactNode>;
} = {
  types: {
    image: () => <div />,
    product: () => <div />,
    adBanner: () => <div />,
  },
};

export const tableOfContentsFrom = (
  str: string
): {
  parsedHtml: ReactNode;
  tableOfContents: TableOfContents;
} => {
  // parse the html string and replace all h2, h3 tags with tag
  // that has an id that is a slug from the value of the tags

  const cleanHtmlString = DOMPurify.sanitize(str, {
    USE_PROFILES: { html: true },
  });

  const tableOfContents: TableOfContents = [];

  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (domNode instanceof ParserElement) {
        if (domNode.name === 'h2' || domNode.name === 'h3') {
          const innerText = (domNode.children[0] as any)?.data;
          const slug = trimTitleToSlug(innerText || '');

          if (domNode.name === 'h2' && slug && slug.length > 0) {
            tableOfContents.push({
              tag: slug,
              text: innerText,
            });
            return (
              <div>
                <Anchor tag={slug}>
                  <h2>{domToReact(domNode.children as any, options)}</h2>
                </Anchor>
              </div>
            );
          }
          if (domNode.name === 'h3' && slug && slug.length > 0) {
            tableOfContents.push({
              tag: slug,
              text: innerText,
            });
            return (
              <div>
                <Anchor tag={slug}>
                  <h3 id={slug}>
                    {domToReact(domNode.children as any, options)}
                  </h3>
                </Anchor>
              </div>
            );
          }
        }
      }
      return domNode;
    },
  };

  const parsedHtml = parse(cleanHtmlString, options);
  return { parsedHtml, tableOfContents };
};
